import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ensureNotificationPermission,
  notificationsEnabled,
  showChatNotification,
} from "@/lib/chatNotifications";

const ChatContext = createContext(null);

const POLL_INTERVAL_MS = 4000;

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [totalUnread, setTotalUnread] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [tick, setTick] = useState(0);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const lastPollRef = useRef(null);
  const timerRef = useRef(null);
  // Snapshot de last_message_at por conv. para detectar nuevos mensajes
  const lastSeenRef = useRef(new Map());
  // Para no notificar la primera vez que cargamos
  const initializedRef = useRef(false);

  const refreshConversations = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/chat/conversations");
      setConversations(data || []);
      const total = (data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
      setTotalUnread(total);
      // Inicializar el snapshot la primera vez para no soltar notificaciones del histórico
      if (!initializedRef.current) {
        const m = new Map();
        (data || []).forEach((c) => m.set(c.id, c.last_message_at || ""));
        lastSeenRef.current = m;
        initializedRef.current = true;
      }
    } catch (_e) {
      /* silent */
    }
  }, [user]);

  const triggerNotificationsFromPoll = useCallback(async (pollData) => {
    if (!user || !notificationsEnabled()) return;
    const updated = (pollData?.conversations || []).filter((c) => {
      if (!c.last_message_at) return false;
      if (c.last_message_sender_id === user.id) return false;
      if (c.is_muted) return false;
      const seen = lastSeenRef.current.get(c.id);
      // Si nunca lo habíamos visto, evitamos disparar notif (es cargas iniciales / nueva conv)
      if (seen === undefined) {
        lastSeenRef.current.set(c.id, c.last_message_at);
        return false;
      }
      return c.last_message_at > seen;
    });
    if (updated.length === 0) return;
    // Pedimos detalle para resolver el nombre del remitente y el título legible
    let allConvs = conversations;
    if (allConvs.length === 0) {
      try {
        const { data } = await api.get("/chat/conversations");
        allConvs = data || [];
      } catch { allConvs = []; }
    }
    updated.forEach((u) => {
      const full = allConvs.find((c) => c.id === u.id);
      const sender = full?.member_details?.find((m) => m.id === u.last_message_sender_id);
      let title = "Chat";
      if (full?.type === "direct") {
        title = sender?.name || "Mensaje nuevo";
      } else if (full?.type === "general") {
        title = `General · ${sender?.name?.split(" ")[0] || "Mensaje"}`;
      } else {
        title = `${full?.name || "Grupo"} · ${sender?.name?.split(" ")[0] || ""}`;
      }
      showChatNotification({
        title,
        body: u.last_message_preview || "Nuevo mensaje",
        tag: `chat-${u.id}`,
        onClick: () => {
          try { window.location.assign(`/chat`); } catch { /* ignore */ }
        },
      });
      lastSeenRef.current.set(u.id, u.last_message_at);
    });
  }, [user, conversations]);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const params = lastPollRef.current ? { since: lastPollRef.current } : {};
      const { data } = await api.get("/chat/poll", { params });
      lastPollRef.current = data.now;
      setTotalUnread(data.total_unread || 0);
      if ((data.conversations || []).length > 0) {
        await triggerNotificationsFromPoll(data);
        await refreshConversations();
        setTick((t) => t + 1);
      }
    } catch (_e) {
      /* silent */
    }
  }, [user, refreshConversations, triggerNotificationsFromPoll]);

  useEffect(() => {
    if (!user) {
      setTotalUnread(0);
      setConversations((prev) => (prev.length === 0 ? prev : []));
      lastSeenRef.current = new Map();
      initializedRef.current = false;
      return;
    }
    refreshConversations();
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    // Pedir permiso al iniciar sesión (una vez)
    ensureNotificationPermission().then((p) => setNotifPermission(p));
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markRead = useCallback(async (conversationId) => {
    try {
      await api.post(`/chat/conversations/${conversationId}/read`);
      await refreshConversations();
    } catch (e) {
      console.warn("[chat] markRead falló:", e?.message || e);
    }
  }, [refreshConversations]);

  const toggleMute = useCallback(async (conversationId, muted) => {
    try {
      await api.post(`/chat/conversations/${conversationId}/mute`, { muted });
      await refreshConversations();
    } catch (e) {
      console.warn("[chat] toggleMute falló:", e?.message || e);
    }
  }, [refreshConversations]);

  const requestNotifications = useCallback(async () => {
    const p = await ensureNotificationPermission(true);
    setNotifPermission(p);
    return p;
  }, []);

  const value = {
    totalUnread,
    conversations,
    tick,
    notifPermission,
    refreshConversations,
    markRead,
    toggleMute,
    requestNotifications,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat fuera de ChatProvider");
  return ctx;
};
